import {
  CheckCircle as CheckCircleIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppListTable from "~/components/AppGeneration/AppListTable";
import CreateAppModal, { type CreateAppData } from "~/components/AppGeneration/CreateAppModal";
import DocumentCard from "~/components/AppGeneration/DocumentCard";
import NewApiWarningModal from "~/components/AppGeneration/NewApiWarningModal";
import Button from "~/components/ui/button";
import { CommonButton } from "~/components/ui/commonButton";
import CreditLackModal from "~/components/ui/creditLackModal";
import Modal from "~/components/ui/modal";
import PageHeader from "~/components/ui/pageHeader";
import { apiBaseUri } from "~/config/apiConfig";
import { DIALOG_MESSAGES, TOAST_MESSAGES } from "~/constants/messages";
import { DocumentApi } from "~/functions/api/document";
import { KintoneAdminApi, type KintoneAdminInfo } from "~/functions/api/kintoneAdmin";
import { type KintoneSystem, KintoneSystemApi } from "~/functions/api/kintoneSystem";
import { OrganizationApi } from "~/functions/api/organization";
import { useNewApiConsent } from "~/hooks/useNewApiConsent";
import DocumentIcon from "~/images/document.svg?react";
import TableIcon from "~/images/table.svg?react";
import { setOrganization } from "~/slices/auth";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

type DocumentStatus = "ready" | "not-ready" | "generating";

interface DocumentInfo {
  id: string;
  status: DocumentStatus;
  modifiedAt?: string;
  createdAt?: string;
  content?: string;
}

const LEGACY_DOC_CUTOFF_JST = new Date("2026-04-14T23:59:59.999+09:00");

const AppGeneration: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentProject, currentUser, organization } = useSelector(
    (state: RootState) => state.auth,
  );
  const [documentInfos, setDocumentInfos] = useState<{
    appDesign: DocumentInfo | null;
    fieldDesign: DocumentInfo | null;
    customizeSpec: DocumentInfo | null;
  }>({
    appDesign: null,
    fieldDesign: null,
    customizeSpec: null,
  });
  const [systems, setSystems] = useState<KintoneSystem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateAppModalOpen, setIsCreateAppModalOpen] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [isNewApiWarningModalOpen, setIsNewApiWarningModalOpen] = useState(false);
  const [kintoneAdminInfo, setKintoneAdminInfo] = useState<KintoneAdminInfo | null>(null);
  const [isCreditConfirmOpen, setIsCreditConfirmOpen] = useState(false);
  const [isCreditLackOpen, setIsCreditLackOpen] = useState(false);
  const [lackCredit, setLackCredit] = useState(0);
  const creditConfirmResolveRef = useRef<((confirmed: boolean) => void) | null>(null);

  const kintoneSystemApi = useRef(new KintoneSystemApi());
  const { hasConsented, markAsConsented } = useNewApiConsent();

  // ドキュメントの存在チェック
  const appDesignId = currentProject?.outputs?.["app-design"]?.id;
  const fieldDesignId = currentProject?.outputs?.["field-design"]?.id;
  const customizeSpecId = currentProject?.outputs?.["customize-specification"]?.id;

  const isAppDesignGenerating = currentProject?.outputs?.["app-design"]?.isGenerating;
  const isFieldDesignGenerating = currentProject?.outputs?.["field-design"]?.isGenerating;
  const isCustomizeSpecGenerating =
    currentProject?.outputs?.["customize-specification"]?.isGenerating;

  // ドキュメント情報とシステム一覧を取得
  const fetchData = useCallback(async () => {
    if (!currentProject?.id || !currentUser?.organization_id) {
      setIsLoading(false);
      return;
    }

    const documentApi = new DocumentApi();

    const results: typeof documentInfos = {
      appDesign: null,
      fieldDesign: null,
      customizeSpec: null,
    };

    // アプリ設計書
    if (appDesignId) {
      try {
        const doc = await documentApi.get(
          currentUser.organization_id,
          currentProject.id,
          appDesignId,
        );
        if (doc) {
          results.appDesign = {
            id: doc.id,
            status: "ready",
            modifiedAt: doc.modified_at || doc.created_at,
            createdAt: doc.created_at,
            content: doc.content,
          };
        }
      } catch (e) {
        console.error("Failed to fetch app design:", e);
      }
    } else if (isAppDesignGenerating) {
      results.appDesign = { id: "", status: "generating" };
    } else {
      results.appDesign = { id: "", status: "not-ready" };
    }

    // フィールド設計書
    if (fieldDesignId) {
      try {
        const doc = await documentApi.get(
          currentUser.organization_id,
          currentProject.id,
          fieldDesignId,
        );
        if (doc) {
          results.fieldDesign = {
            id: doc.id,
            status: "ready",
            modifiedAt: doc.modified_at || doc.created_at,
            createdAt: doc.created_at,
            content: doc.content,
          };
        }
      } catch (e) {
        console.error("Failed to fetch field design:", e);
      }
    } else if (isFieldDesignGenerating) {
      results.fieldDesign = { id: "", status: "generating" };
    } else {
      results.fieldDesign = { id: "", status: "not-ready" };
    }

    // カスタマイズ仕様書
    if (customizeSpecId) {
      try {
        const doc = await documentApi.get(
          currentUser.organization_id,
          currentProject.id,
          customizeSpecId,
        );
        if (doc) {
          results.customizeSpec = {
            id: doc.id,
            status: "ready",
            modifiedAt: doc.modified_at || doc.created_at,
            createdAt: doc.created_at,
            content: doc.content,
          };
        }
      } catch (e) {
        console.error("Failed to fetch customize spec:", e);
      }
    } else if (isCustomizeSpecGenerating) {
      results.customizeSpec = { id: "", status: "generating" };
    } else {
      results.customizeSpec = { id: "", status: "not-ready" };
    }

    // kintoneシステム一覧を取得
    try {
      const fetchedSystems = await kintoneSystemApi.current.getKintoneSystemsByProject(
        currentUser.organization_id,
        currentProject.id,
      );
      if (fetchedSystems) {
        setSystems(fetchedSystems);
      }
    } catch (e) {
      console.error("Failed to fetch systems:", e);
    }

    // 組織情報を最新化
    try {
      const organizationApi = new OrganizationApi();
      const latestOrg = await organizationApi.get(currentUser.organization_id);
      if (latestOrg) {
        dispatch(setOrganization(latestOrg));
      }
    } catch (e) {
      console.error("Failed to fetch organization:", e);
    }

    // kintoneAdmin情報を取得
    try {
      const kintoneAdminApi = new KintoneAdminApi();
      const adminInfo = await kintoneAdminApi.getKintoneAdmin(
        currentUser.organization_id,
        currentProject.id,
      );
      setKintoneAdminInfo(adminInfo);
    } catch (e) {
      console.error("Failed to fetch kintone admin:", e);
    }

    setDocumentInfos(results);
    setIsLoading(false);
  }, [
    currentProject?.id,
    currentUser?.organization_id,
    appDesignId,
    fieldDesignId,
    customizeSpecId,
    isAppDesignGenerating,
    isFieldDesignGenerating,
    isCustomizeSpecGenerating,
    dispatch,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 必須ドキュメントが揃っているか
  const isDocumentsReady = documentInfos.fieldDesign?.status === "ready";

  const isLegacyCreatedAt = (createdAt: string | undefined): boolean => {
    if (!createdAt) return false;
    const createdAtDate = new Date(createdAt);
    if (Number.isNaN(createdAtDate.getTime())) return false;
    return createdAtDate.getTime() <= LEGACY_DOC_CUTOFF_JST.getTime();
  };

  const isLegacyDocument = (doc: DocumentInfo | null): boolean => {
    if (doc?.status !== "ready") return false;
    return isLegacyCreatedAt(doc.createdAt);
  };

  const isLegacyAnyDocument =
    isLegacyDocument(documentInfos.appDesign) ||
    isLegacyDocument(documentInfos.fieldDesign) ||
    isLegacyDocument(documentInfos.customizeSpec);

  // 本番環境のみ旧世代ドキュメントチェックでブロック
  const isProductionEnv = apiBaseUri.includes("api-gateway-prod");
  const shouldBlockLegacyDocument = isProductionEnv && isLegacyAnyDocument;

  // URLパラメータからモーダルを自動で開く
  useEffect(() => {
    if (searchParams.get("openModal") === "true" && !isLoading) {
      setSearchParams({});
      // 旧世代ドキュメントの場合はトーストを表示してモーダルを開かない
      if (shouldBlockLegacyDocument) {
        toast.error(TOAST_MESSAGES.APP_DEVELOPMENT.OLD_DOCUMENT_ERROR);
        return;
      }
      if (hasConsented()) {
        setIsCreateAppModalOpen(true);
      } else {
        setIsNewApiWarningModalOpen(true);
      }
    }
  }, [searchParams, setSearchParams, isLoading, hasConsented, shouldBlockLegacyDocument]);

  const openCreateAppFlow = () => {
    if (hasConsented()) {
      setIsCreateAppModalOpen(true);
    } else {
      setIsNewApiWarningModalOpen(true);
    }
  };

  const handleCreateApp = () => {
    // 旧世代ドキュメントの場合はトーストを表示してアプリ生成を中断
    if (shouldBlockLegacyDocument) {
      toast.error(TOAST_MESSAGES.APP_DEVELOPMENT.OLD_DOCUMENT_ERROR);
      return;
    }
    openCreateAppFlow();
  };

  const handleCreditConfirm = () => {
    setIsCreditConfirmOpen(false);
    creditConfirmResolveRef.current?.(true);
    creditConfirmResolveRef.current = null;
  };

  const handleCreditConfirmCancel = () => {
    setIsCreditConfirmOpen(false);
    creditConfirmResolveRef.current?.(false);
    creditConfirmResolveRef.current = null;
  };

  const handleCloseCreateAppModal = () => {
    setIsCreateAppModalOpen(false);
  };

  const handleRegenerateDocuments = () => {
    if (!currentProject?.id || !currentUser?.organization_id) return;
    toast.warning(TOAST_MESSAGES.APP_DEVELOPMENT.REGENERATE_DOCUMENT);
    navigate(`/${currentUser.organization_id}/project/${currentProject.id}`);
  };

  const handleNewApiWarningConfirm = () => {
    markAsConsented();
    setIsNewApiWarningModalOpen(false);
    setIsCreateAppModalOpen(true);
  };

  const handleNewApiWarningClose = () => {
    setIsNewApiWarningModalOpen(false);
  };

  const handleSubmitCreateApp = async (data: CreateAppData) => {
    if (!currentProject?.id || !currentUser?.organization_id || !currentUser?.id) return;
    if (isCreatingJob) return;

    // 最新の組織情報を取得してクレジット判定を行う
    const organizationApi = new OrganizationApi();
    const latestOrg = await organizationApi.get(currentUser.organization_id);
    if (!latestOrg) return;
    dispatch(setOrganization(latestOrg));

    let useCredit = false;

    if (latestOrg.remainingAppGenerationCount <= 0) {
      const creditCost = latestOrg.plan.limits.appGenerationCreditCost;
      if (latestOrg.remainingCredit >= creditCost) {
        // クレジット十分 → 確認ダイアログを表示して応答を待つ
        const confirmed = await new Promise<boolean>((resolve) => {
          creditConfirmResolveRef.current = resolve;
          setIsCreditConfirmOpen(true);
        });
        if (!confirmed) return;
        useCredit = true;
      } else {
        // クレジット不足
        setIsCreateAppModalOpen(false);
        setLackCredit(creditCost - latestOrg.remainingCredit);
        setIsCreditLackOpen(true);
        return;
      }
    }

    setIsCreatingJob(true);

    try {
      const result = await kintoneSystemApi.current.startKintoneSystem(
        currentUser.organization_id,
        currentProject.id,
        {
          subdomain: data.credentials.subdomain,
          space_name: data.spaceName,
          login_id: data.credentials.userId,
          password: data.credentials.password,
          include_sample_data: data.includeSampleData,
          ...(data.selectedCustomizeIds !== undefined && {
            selected_customize_ids: data.selectedCustomizeIds,
          }),
          ...(useCredit && { use_credit: true }),
        },
      );

      if (result) {
        setIsCreateAppModalOpen(false);
        toast.success(TOAST_MESSAGES.APP_DEVELOPMENT.START_SUCCESS);

        if (useCredit) {
          dispatch(
            setOrganization({
              ...latestOrg,
              remainingCredit: Math.max(
                0,
                latestOrg.remainingCredit - latestOrg.plan.limits.appGenerationCreditCost,
              ),
            }),
          );
        }

        navigate(
          `/${currentUser.organization_id}/project/${currentProject.id}/app-generation/${result.id}`,
        );
      }
    } catch (error: unknown) {
      console.error("Failed to create development job:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(TOAST_MESSAGES.APP_DEVELOPMENT.START_FAILED(errorMessage));
    } finally {
      setIsCreatingJob(false);
    }
  };

  const handleDocumentClick = (type: string, id: string | undefined) => {
    if (!id || !currentProject?.id || !currentUser?.organization_id) return;
    navigate(`/${currentUser.organization_id}/project/${currentProject.id}/${type}/${id}`);
  };

  const handleSystemClick = (systemId: string) => {
    if (!currentProject?.id || !currentUser?.organization_id) return;
    navigate(
      `/${currentUser.organization_id}/project/${currentProject.id}/app-generation/${systemId}`,
    );
  };

  if (isLoading) {
    return (
      <div className={styles["container"]}>
        <PageHeader
          title="アプリ生成"
          titleActions={<CommonButton disabled>アプリを作る</CommonButton>}
        />
        <div className={styles["content"]}>
          <div className={styles["loadingState"]}>読み込み中...</div>
        </div>
      </div>
    );
  }

  // ボタン無効化の理由を取得（フィールド設計書未生成時のみ）
  const disableReason = !isDocumentsReady ? "フィールド設計書が必要です" : null;
  const isButtonDisabled = !isDocumentsReady;

  const creditModals = (
    <>
      <Modal
        label={DIALOG_MESSAGES.CREDIT_CONSUME.APP_GENERATION_TITLE}
        isOpen={isCreditConfirmOpen}
        onClose={handleCreditConfirmCancel}
        usePortal
        content={
          <div className={styles["creditConfirmContent"]}>
            <p className={styles["creditConfirmText"]}>
              {DIALOG_MESSAGES.CREDIT_CONSUME.APP_GENERATION_BODY(
                organization.plan.limits.appGenerationCreditCost,
              )}
            </p>
            <p className={styles["creditConfirmText"]}>
              {DIALOG_MESSAGES.CREDIT_CONSUME.APP_GENERATION_REMAINING_CREDIT(
                organization.remainingCredit,
              )}
            </p>
            <div className={styles["creditConfirmButtons"]}>
              <Button
                styleType="white-middle"
                label="キャンセル"
                onClick={handleCreditConfirmCancel}
              />
              <Button styleType="blue-middle" label="生成する" onClick={handleCreditConfirm} />
            </div>
          </div>
        }
      />

      <CreditLackModal
        lackCredit={lackCredit}
        isOpen={isCreditLackOpen}
        onClose={() => setIsCreditLackOpen(false)}
      />
    </>
  );

  // システム一覧がある場合
  if (systems.length > 0) {
    return (
      <div className={styles["container"]}>
        <PageHeader
          title="アプリ生成"
          titleActions={
            <Tooltip title={disableReason || ""} arrow disableHoverListener={!isButtonDisabled}>
              <span>
                <CommonButton onClick={handleCreateApp} disabled={isButtonDisabled}>
                  アプリを作る
                </CommonButton>
              </span>
            </Tooltip>
          }
        />

        <div className={styles["content"]}>
          <AppListTable systems={systems} onRowClick={handleSystemClick} />
        </div>

        <CreateAppModal
          isOpen={isCreateAppModalOpen}
          onClose={handleCloseCreateAppModal}
          onSubmit={handleSubmitCreateApp}
          onRegenerate={handleRegenerateDocuments}
          kintoneAdminInfo={kintoneAdminInfo}
        />

        <NewApiWarningModal
          isOpen={isNewApiWarningModalOpen}
          onClose={handleNewApiWarningClose}
          onConfirm={handleNewApiWarningConfirm}
        />

        {creditModals}
      </div>
    );
  }

  // アプリ未作成の場合
  const requiredPointItems = [
    "設計書に記載されているアプリ名／フィールド名でアプリが生成されます",
    "レクシンはアプリとフィールド（フォーム）のみを生成します",
    "その他の設定は生成後にkintoneで行ってください",
  ];

  const optionalPointItems = [
    "レクシンが対応できるカスタマイズは、カスタマイズ仕様書の形式に依存します",
    "まずはカスタマイズ仕様書を登録してから、必要に応じて内容を調整してください",
  ];

  return (
    <div className={styles["container"]}>
      <PageHeader
        title="アプリ生成"
        titleActions={
          <Tooltip title={disableReason || ""} arrow disableHoverListener={!isButtonDisabled}>
            <span>
              <CommonButton onClick={handleCreateApp} disabled={isButtonDisabled}>
                アプリを作る
              </CommonButton>
            </span>
          </Tooltip>
        }
      />

      <div className={styles["content"]}>
        <p className={styles["description"]}>
          アプリ設計書とフィールド設計書を基にkintoneアプリを生成することができます。
          ドキュメントを確認して「アプリを作る」ボタンをクリックしましょう。
        </p>

        {/* 必須ドキュメントセクション */}
        <div className={styles["sectionCard"]}>
          <div className={styles["sectionHeader"]}>
            <h2 className={styles["sectionTitle"]}>必須ドキュメント</h2>
            {isDocumentsReady && (
              <span className={styles["readyBadge"]}>
                <CheckCircleIcon sx={{ width: 16, height: 16 }} />
                準備完了
              </span>
            )}
          </div>

          <p className={styles["sectionDescription"]}>
            アプリ設計書とフィールド設計書の内容をもとに、作成されるアプリの構成が決まります。必要に応じて中身を確認してから、アプリ作成に進んでください。
          </p>

          <div className={styles["sectionBody"]}>
            <div className={styles["sectionMain"]}>
              <div className={styles["documentGrid"]}>
                <DocumentCard
                  title="アプリ設計書"
                  icon={<DocumentIcon />}
                  status={documentInfos.appDesign?.status || "not-ready"}
                  modifiedAt={documentInfos.appDesign?.modifiedAt}
                  content={documentInfos.appDesign?.content}
                  documentType="app-design"
                  onClick={() => handleDocumentClick("app-design", appDesignId)}
                />
                <DocumentCard
                  title="フィールド設計書"
                  icon={<TableIcon />}
                  status={documentInfos.fieldDesign?.status || "not-ready"}
                  modifiedAt={documentInfos.fieldDesign?.modifiedAt}
                  content={documentInfos.fieldDesign?.content}
                  documentType="field-design"
                  onClick={() => handleDocumentClick("field-design", fieldDesignId)}
                />
              </div>
            </div>

            <aside className={styles["pointCard"]}>
              <div className={styles["pointTitle"]}>
                <LightbulbOutlinedIcon className={styles["pointIcon"] ?? ""} aria-hidden="true" />
                POINT
              </div>
              <ul className={styles["pointList"]}>
                {requiredPointItems.map((text) => (
                  <li key={text} className={styles["pointItem"]}>
                    {text}
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </div>

        {/* 任意ドキュメントセクション */}
        <div className={styles["sectionCard"]}>
          <div className={styles["sectionHeader"]}>
            <h2 className={styles["sectionTitle"]}>任意ドキュメント</h2>
          </div>

          <p className={styles["sectionDescription"]}>
            カスタマイズ仕様書があれば、レクシンがコードを生成してアプリにカスタマイズを適用します。
          </p>

          <div className={styles["sectionBody"]}>
            <div className={styles["sectionMain"]}>
              <div className={styles["documentGridOptional"]}>
                <DocumentCard
                  title="カスタマイズ仕様書"
                  icon={<TableIcon />}
                  status={documentInfos.customizeSpec?.status || "not-ready"}
                  modifiedAt={documentInfos.customizeSpec?.modifiedAt}
                  content={documentInfos.customizeSpec?.content}
                  documentType="customize-specification"
                  onClick={() => handleDocumentClick("customize-specification", customizeSpecId)}
                />
                {documentInfos.customizeSpec?.status === "ready" &&
                  !kintoneAdminInfo?.is_registered && (
                    <div className={styles["kintoneAdminAction"]}>
                      <button
                        type="button"
                        className={styles["kintoneAdminButton"]}
                        onClick={() => {
                          if (currentUser?.organization_id && currentProject?.id) {
                            navigate(
                              `/${currentUser.organization_id}/project/${currentProject.id}/settings`,
                            );
                          }
                        }}
                      >
                        kintoneシステム管理者を登録
                      </button>
                      <p className={styles["kintoneAdminNote"]}>
                        <WarningAmberIcon className={styles["kintoneAdminNoteIcon"] ?? ""} />
                        カスタマイズの適用にはkintoneシステム管理権限を持つkintoneアカウントが必要です
                      </p>
                    </div>
                  )}
              </div>
            </div>

            <aside className={styles["pointCard"]}>
              <div className={styles["pointTitle"]}>
                <LightbulbOutlinedIcon className={styles["pointIcon"] ?? ""} aria-hidden="true" />
                POINT
              </div>
              <ul className={styles["pointList"]}>
                {optionalPointItems.map((text) => (
                  <li key={text} className={styles["pointItem"]}>
                    {text}
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </div>

      <CreateAppModal
        isOpen={isCreateAppModalOpen}
        onClose={handleCloseCreateAppModal}
        onSubmit={handleSubmitCreateApp}
        onRegenerate={handleRegenerateDocuments}
        kintoneAdminInfo={kintoneAdminInfo}
      />

      <NewApiWarningModal
        isOpen={isNewApiWarningModalOpen}
        onClose={handleNewApiWarningClose}
        onConfirm={handleNewApiWarningConfirm}
      />

      {creditModals}
    </div>
  );
};

export default AppGeneration;
